import { Connection } from '@bentley/pw-wac';
import React from 'react';
import { IframeMessageHost } from '../../embed/iframeMessageHost';
import { AuthService } from '../../services';
import { IModel } from '../../types';
import {
  embedPwWebComponent,
  embedShareComponent,
  getLinkToTab,
  isElectron,
  openCenteredPopup,
} from '../../utils';
import { embedDesignReviewComponent } from './DesignReview/DesignReview';
import { FRAME_ID } from './TeamsFrame';
import { TeamsFrameContainerLayout } from './TeamsFrameContainerLayout';

export interface TeamsFrameContainerProps {
  frameUrl?: string;
  linkToUrl?: string;
  connectionName: string;
  connectionId: string;
  serviceName?: string;
  setTabIsDefault: (value: boolean) => any;
  projectId: string;
  subEntityId?: string;
  iModel?: IModel;
}

interface TeamsFrameContainerState {
  iframeMessageHost: IframeMessageHost;
  authPopup: Window | null;
  authWindowMonitor: any;
  authExternalCallback?: (e: any) => void;
  authExternalSuccess: boolean;
  authExternalSuccessCallback?: (_: void) => void;
  connection?: Connection;
}

/**
 * Sets up an embedded service and communicates with it using {@link IframeMessageHost}.
 * Most of the setup consists of handling authentication.
 *
 * If service is not authenticated it sends a message to {@link IframeMessageHost} and
 * it initiates authentication flow. A pop-up window is opened and that navigates to URL provided by a service.
 * If authentication was successful, authentication provider redirects to service's homepage in the same pop-up.
 * Then service calls {@link IframeMessageHost} again and asks to close a pop-up and do a refresh.
 *
 * Authentication can also be done by sending tokens to embedded service where it delegates
 * them in its back-end server. This (mostly) eliminates the need of additional login screen and pop-ups.
 */
export class TeamsFrameContainer extends React.Component<
  TeamsFrameContainerProps,
  TeamsFrameContainerState
> {
  static defaultProps: Partial<TeamsFrameContainerProps> = {
    frameUrl: undefined,
    serviceName: '',
    connectionName: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      iframeMessageHost: new IframeMessageHost(),
      authPopup: null,
      authWindowMonitor: undefined,
      authExternalCallback: undefined,
      authExternalSuccess: false,
      authExternalSuccessCallback: undefined,
    };
  }

  async componentDidMount() {
    const container = document.getElementById(FRAME_ID);
    const url = this.props.frameUrl;

    try {
      this.registerEmbedEvents();
      this.state.iframeMessageHost.listenForMessages();

      if (
        url &&
        container &&
        this.props.serviceName &&
        !embedShareComponent(
          this.props.serviceName,
          this.props.connectionName
        ) &&
        !embedPwWebComponent(this.props.serviceName) &&
        !embedDesignReviewComponent(this.props.serviceName)
      ) {
        this.state.iframeMessageHost.embedPage(container, url, 10000);
        this.state.iframeMessageHost.postMessages(this.props.serviceName);
      }
    } catch (error) {
      console.error(error);
    }
  }

  registerEmbedEvents() {
    const iframeMessageHost = this.state.iframeMessageHost;

    iframeMessageHost.onAuthenticationRequested = (
      uri: string,
      onSuccess?: (_: void) => void
    ) => {
      if (onSuccess === undefined) {
        this.setState({
          authExternalCallback: this.getAuthExternalCallback(
            encodeURIComponent(uri)
          ),
          authExternalSuccessCallback: undefined,
        });
      } else {
        // If `onSuccess` callback is provided, we don't show a login screen
        // but invoke the callback when authentication is completed instead
        // of reloading the page. It is useful when some service inside a
        // framed app needs to authenticate.
        this.setState({
          authExternalSuccessCallback: onSuccess,
        });
        this.getAuthExternalCallback(encodeURIComponent(uri))(undefined);
      }
    };

    iframeMessageHost.onAuthenticationSuccessful = () => {
      if (!this.state.authPopup && isElectron()) return;
      this.setState({ authExternalSuccess: true });
      this.finishAuthentication();
    };

    iframeMessageHost.onLogOutRequested = async (expired?) => {
      expired
        ? await AuthService.signout('Your session has expired. Logging out...')
        : await AuthService.signout();
    };

    iframeMessageHost.onHandshakeReply = () => {
      if (this.state.authExternalSuccess)
        iframeMessageHost.postMessages(this.props.serviceName!);
    };

    iframeMessageHost.onAccessTokenRequested = async () =>
      (await AuthService.getUser()).access_token;

    iframeMessageHost.onIdTokenRequested = async () =>
      (await AuthService.getUser()).id_token;

    iframeMessageHost.onTabLinkRequested = async () => getLinkToTab();

    this.setState({ iframeMessageHost });
  }

  getAuthExternalCallback = (authUrl?: string) => e => {
    e?.preventDefault();
    // It is "tough" to keep a handle on the opened pop-up from the main window
    // when in Electron, so we use microsoftTeams.authentication.authenticate().
    if (isElectron()) {
      microsoftTeams.authentication.authenticate({
        height: 600,
        url: `/home/authorizeExternal?authUrl=${authUrl}`,

        successCallback: response => {
          if (this.state.authExternalSuccessCallback !== undefined) {
            this.state.authExternalSuccessCallback();
          } else {
            this.setState(
              {
                authExternalSuccess: true,
                authExternalCallback: undefined,
              },
              () => {
                this.state.iframeMessageHost.reloadEmbeddedPage(
                  this.props.serviceName,
                  this.props.frameUrl
                );
              }
            );
          }
        },
        failureCallback: e => {
          this.handlePopUpFailure(e);
        },
      });
    } else {
      try {
        this.setState(
          {
            authPopup: openCenteredPopup(authUrl),
          },
          () => {
            this.startAuthWindowMonitor();
          }
        );
      } catch (error) {
        console.error(error);
        this.handlePopUpFailure(error);
      }
    }
  };

  startAuthWindowMonitor = () => {
    this.setState((state, props) => {
      return {
        authWindowMonitor: window.setInterval(() => {
          if (!state.authPopup || state.authPopup.closed) {
            this.handlePopUpFailure('CancelledByUser');
          }
        }),
      };
    });
  };

  stopAuthWindowMonitor() {
    if (this.state.authWindowMonitor) {
      window.clearInterval(this.state.authWindowMonitor);
    }
  }

  finishAuthentication() {
    this.state.authPopup?.close();
    this.stopAuthWindowMonitor();
    this.setState({ authExternalCallback: undefined }, () => {
      this.state.authExternalSuccessCallback
        ? this.state.authExternalSuccessCallback()
        : this.state.iframeMessageHost.reloadEmbeddedPage(
            this.props.serviceName,
            this.props.frameUrl
          );
    });
  }

  handlePopUpFailure(error: any) {
    console.warn('Authentication pop-up failure', error);
    this.stopAuthWindowMonitor();
    if (this.state.authExternalSuccessCallback === undefined) {
      this.setState({ authExternalCallback: undefined });
    }
  }

  render() {
    return (
      <TeamsFrameContainerLayout
        serviceName={this.props.serviceName}
        connectionName={this.props.connectionName}
        linkToUrl={this.props.linkToUrl}
        projectId={this.props.projectId}
        frameUrl={this.props.frameUrl}
        subEntityId={this.props.subEntityId}
        authExternalCallback={this.state.authExternalCallback}
        iModel={this.props.iModel}
      />
    );
  }
}
